import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import { CardsSphereRequest } from 'types/card/card-sphere';
import {
    useGetSphereDataQuery,
    useEditSphereMutation,
} from 'store/slices/cards/sphere/edit-sphere';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    setOpenEditSphereDialog: React.Dispatch<React.SetStateAction<boolean>>;
    sphereId: string;
    refetch: any;
}

const EditCardsSphere: React.FC<Props> = (props: Props) => {
    const { setOpenEditSphereDialog, sphereId, refetch } = props;
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<CardsSphereRequest>();

    const { data, isSuccess: dataIsSuccess } = useGetSphereDataQuery(sphereId, {
        refetchOnMountOrArgChange: true,
    });

    const [editSphere, { error, isError, isSuccess, isLoading }] =
        useEditSphereMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();

    const onSubmit = (values: CardsSphereRequest) => {
        editSphere({ sphereTitle: values.sphereTitle, cardSphereId: sphereId });
    };

    const closeModal = () => {
        setOpenEditSphereDialog(false);
        reset();
    };

    useEffect(() => {
        if (isSuccess) {
            setOpenEditSphereDialog(false);
            refetch();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    EDIT CARD SPHERE
                </DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                {dataIsSuccess && (
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box pt={pxToRem(15)} textAlign={'center'}>
                            <Controller
                                name="sphereTitle"
                                control={control}
                                rules={{ required: 'Title is required' }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <TextField
                                        sx={{ width: '80%' }}
                                        name={name}
                                        value={value ?? data.title}
                                        onChange={onChange}
                                        label="Title"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Edit Card Sphere
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeModal()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Edit an Sphere!'}
            />
        </>
    );
};
export default EditCardsSphere;
